import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeInUp, SlideInLeft, SlideInRight } from "./PageTransition";

export default function SmartContact({ dark }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    budget: "",
    timeline: "",
    message: ""
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const services = [
    "Website Development",
    "Mobile App Development", 
    "UI/UX Design",
    "Digital Marketing",
    "Business Consulting",
    "Custom Software",
    "Other"
  ];

  const budgets = [
    "Under ₹20,000",
    "₹20,000 - ₹50,000",
    "₹50,000 - ₹1,00,000", 
    "₹1,00,000 - ₹2,50,000",
    "₹2,50,000 - ₹5,00,000",
    "Above ₹5,00,000"
  ];

  const timelines = [
    "1-2 weeks",
    "2-4 weeks",
    "1-2 months", 
    "2-3 months",
    "3+ months"
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.service) {
      newErrors.service = "Please select a service";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Prepare FormData
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });
    uploadedFiles.forEach(fileObj => {
      formDataToSend.append('files[]', fileObj.file);
    });

    try {
      const response = await fetch('/upload.php', {
        method: 'POST',
        body: formDataToSend,
      });
      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: "", email: "", phone: "", company: "",
            service: "", budget: "", timeline: "", message: ""
          });
          setUploadedFiles([]);
        }, 5000);
      } else {
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      alert('Submission failed. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'application/zip',
    'application/x-zip-compressed',
    'application/x-rar-compressed',
    'application/octet-stream', // for some zip/rar
  ];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFileUpload = (files) => {
    const newFiles = [];
    for (const file of Array.from(files)) {
      if (!allowedTypes.includes(file.type) || file.size > maxFileSize) {
        alert(`${file.name} is not a supported file type or exceeds 10MB.`);
        continue;
      }
      newFiles.push({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file
      });
    }
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isSubmitted) {
    return (
      <section className="relative py-20 px-4 overflow-hidden mb-0 pb-0">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-green-900/20 dark:via-[#1b1c4e]/30 dark:to-emerald-900/20" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-4xl shadow-2xl"
          >
            ✅
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-3xl font-bold text-[#1b1c4e] dark:text-white mb-4"
          >
            Project Request Submitted!
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-lg text-[#23244d] dark:text-white/80 mb-8"
          >
            Thank you for your detailed project request. We'll analyze your requirements and get back to you within 24 hours with a customized proposal!
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="space-y-4"
          >
            <div className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full ${
              dark ? 'bg-[#23244d]/80 text-white' : 'bg-white/80 text-[#1b1c4e]'
            } shadow-lg backdrop-blur-md border border-green-500/30`}>
              <span className="text-lg">📧</span>
              <span>Check your email for confirmation</span>
            </div>
            
            <div className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full ${
              dark ? 'bg-[#23244d]/80 text-white' : 'bg-white/80 text-[#1b1c4e]'
            } shadow-lg backdrop-blur-md border border-blue-500/30`}>
              <span className="text-lg">📱</span>
              <span>We'll also send you a WhatsApp message</span>
            </div>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            onClick={() => setIsSubmitted(false)}
            className="mt-8 px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
          >
            Submit Another Request
          </motion.button>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="relative py-20 px-4 overflow-hidden mb-0 pb-4">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-indigo-900/20 dark:via-[#1b1c4e]/30 dark:to-purple-900/20" />
      
      <div className="relative max-w-6xl mx-auto">
        <FadeInUp className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1b1c4e] dark:text-white mb-4">
            Smart Project Request
          </h2>
          <p className="text-lg text-[#23244d] dark:text-white/80 max-w-2xl mx-auto">
            Tell us about your project and we'll create a customized solution for your business
          </p>
        </FadeInUp>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Project Information */}
          <SlideInLeft delay={0.2}>
            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-[#1b1c4e] dark:text-white mb-6">
                Project Details
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    dark ? 'text-white/80' : 'text-[#23244d]/80'
                  }`}>
                    Service Required *
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                      errors.service 
                        ? 'border-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:border-indigo-500'
                    } ${
                      dark 
                        ? 'bg-[#1b1c4e]/50 text-white' 
                        : 'bg-white text-[#1b1c4e]'
                    }`}
                  >
                    <option value="">Select a service</option>
                    {services.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                  {errors.service && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.service}
                    </motion.p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Budget Range
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-indigo-500 transition-all duration-300 ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white' 
                          : 'bg-white text-[#1b1c4e]'
                      }`}
                    >
                      <option value="">Select budget</option>
                      {budgets.map(budget => (
                        <option key={budget} value={budget}>{budget}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Timeline
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-indigo-500 transition-all duration-300 ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white' 
                          : 'bg-white text-[#1b1c4e]'
                      }`}
                    >
                      <option value="">Select timeline</option>
                      {timelines.map(timeline => (
                        <option key={timeline} value={timeline}>{timeline}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* File Upload Area */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    dark ? 'text-white/80' : 'text-[#23244d]/80'
                  }`}>
                    Project Files (Optional)
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                      isDragOver 
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                        : 'border-gray-300 hover:border-indigo-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="text-4xl mb-4">📁</div>
                    <p className={`text-sm ${
                      dark ? 'text-white/70' : 'text-[#23244d]/70'
                    }`}>
                      Drag & drop files here or{' '}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        browse files
                      </button>
                    </p>
                    <p className={`text-xs mt-2 ${
                      dark ? 'text-white/50' : 'text-[#23244d]/50'
                    }`}>
                      Max 10MB per file • PDF, DOC, JPG, PNG, Zip, Rar
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="hidden"
                    />
                  </div>

                  {/* Uploaded Files */}
                  <AnimatePresence>
                    {uploadedFiles.map((file) => (
                      <motion.div
                        key={file.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className={`flex items-center justify-between p-3 mt-2 rounded-lg ${
                          dark ? 'bg-[#23244d]/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-lg">📄</span>
                          <div>
                            <p className={`text-sm font-medium ${
                              dark ? 'text-white' : 'text-[#1b1c4e]'
                            }`}>
                              {file.name}
                            </p>
                            <p className={`text-xs ${
                              dark ? 'text-white/60' : 'text-[#23244d]/60'
                            }`}>
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(file.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          ✕
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </SlideInLeft>

          {/* Contact Form */}
          <SlideInRight delay={0.4}>
            <motion.form
              onSubmit={handleSubmit}
              className={`p-8 rounded-2xl shadow-xl backdrop-blur-md border-2 ${
                dark 
                  ? 'bg-[#23244d]/90 border-indigo-400/30' 
                  : 'bg-white/90 border-indigo-500/30'
              }`}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className={`text-2xl font-bold mb-6 ${
                dark ? 'text-white' : 'text-[#1b1c4e]'
              }`}>
                Contact Information
              </h3>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                        errors.name 
                          ? 'border-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:border-indigo-500'
                      } ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white placeholder-white/50' 
                          : 'bg-white text-[#1b1c4e] placeholder-gray-500'
                      }`}
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.name}
                      </motion.p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                        errors.email 
                          ? 'border-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:border-indigo-500'
                      } ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white placeholder-white/50' 
                          : 'bg-white text-[#1b1c4e] placeholder-gray-500'
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.email}
                      </motion.p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-indigo-500 transition-all duration-300 ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white placeholder-white/50' 
                          : 'bg-white text-[#1b1c4e] placeholder-gray-500'
                      }`}
                      placeholder="+91 8408993879"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      dark ? 'text-white/80' : 'text-[#23244d]/80'
                    }`}>
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-indigo-500 transition-all duration-300 ${
                        dark 
                          ? 'bg-[#1b1c4e]/50 text-white placeholder-white/50' 
                          : 'bg-white text-[#1b1c4e] placeholder-gray-500'
                      }`}
                      placeholder="Your company"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    dark ? 'text-white/80' : 'text-[#23244d]/80'
                  }`}>
                    Project Description *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                      errors.message 
                        ? 'border-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:border-indigo-500'
                    } ${
                      dark 
                        ? 'bg-[#1b1c4e]/50 text-white placeholder-white/50' 
                        : 'bg-white text-[#1b1c4e] placeholder-gray-500'
                    }`}
                    placeholder="Describe your project requirements, goals, and any specific features you need..."
                  />
                  {errors.message && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.message}
                    </motion.p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                  }`}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                      <span>Processing Request...</span>
                    </div>
                  ) : (
                    "Submit Project Request"
                  )}
                </motion.button>
              </div>
            </motion.form>
          </SlideInRight>
        </div>
      </div>
    </section>
  );
} 